// src/components/gamification/GamificationWidget.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Flame, TrendingUp, Award, Zap } from 'lucide-react';
import { useGamification } from '../../stores/gamificationStore';
import Card from '../shared/Card';
import { achievements } from '../../data/gamification';

const GamificationWidget = () => {
  const {
    totalPoints,
    getCurrentLevelData,
    getPointsToNextLevel,
    unlockedAchievements,
    currentStreak,
    longestStreak,
  } = useGamification();

  const levelInfo = getCurrentLevelData();
  const pointsToNext = getPointsToNextLevel();
  const progressPercentage = levelInfo.percentageToNextLevel;

  // Get recent achievements (last 3)
  const recentAchievements = unlockedAchievements
    .slice(-3)
    .reverse()
    .map(id => achievements[id])
    .filter(Boolean);

  const getLevelColor = (level) => {
    const colors = {
      1: 'from-slate-500 to-slate-600',
      2: 'from-blue-500 to-blue-600',
      3: 'from-cyan-500 to-cyan-600',
      4: 'from-teal-500 to-teal-600',
      5: 'from-green-500 to-green-600',
      6: 'from-amber-500 to-amber-600',
      7: 'from-orange-500 to-orange-600',
      8: 'from-red-500 to-red-600',
      9: 'from-purple-500 to-purple-600',
      10: 'from-pink-500 to-pink-600',
    };
    return colors[level] || colors[1];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="mb-8"
    >
      <Card className="p-6 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Level & Progress */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-16 h-16 bg-gradient-to-br ${getLevelColor(levelInfo.currentLevel.level)} rounded-2xl flex items-center justify-center shadow-lg`}>
                  <levelInfo.currentLevel.icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold text-white">
                      Level {levelInfo.currentLevel.level}
                    </h3>
                    {currentStreak > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 rounded-lg border border-orange-500/30">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <span className="text-orange-400 font-bold text-sm">{currentStreak}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-indigo-300 font-semibold">{levelInfo.currentLevel.title}</p>
                  <p className="text-slate-400 text-sm">{totalPoints.toLocaleString()} points</p>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {levelInfo.nextLevel && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">Progress to Level {levelInfo.nextLevel.level}</span>
                  <span className="text-indigo-300 font-bold">{progressPercentage}%</span>
                </div>
                <div className="relative">
                  <div
                    className="w-full bg-slate-700 rounded-full h-3"
                    role="progressbar"
                    aria-valuenow={progressPercentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Progress to next level"
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className={`h-3 bg-gradient-to-r ${getLevelColor(levelInfo.currentLevel.level)} rounded-full relative overflow-hidden`}
                    >
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                    </motion.div>
                  </div>
                </div>
                <p className="text-slate-400 text-xs">
                  {pointsToNext.toLocaleString()} points to next level
                </p>
              </div>
            )}

            {levelInfo.currentLevel.level === 10 && (
              <div className="mt-4 p-3 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-lg border border-pink-500/30">
                <p className="text-pink-300 text-sm font-semibold flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Max Level Achieved. You're a Lover of Wisdom, a philosopher, literally.
                </p>
              </div>
            )}
          </div>

          {/* Stats & Achievements */}
          <div className="space-y-4">
            {/* Streak Info */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Current Streak</span>
                <Flame className="w-5 h-5 text-orange-400" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">{currentStreak}</span>
                <span className="text-slate-400 text-sm">days</span>
              </div>
              {longestStreak > currentStreak && (
                <p className="text-slate-400 text-xs mt-1">
                  Best: {longestStreak} days
                </p>
              )}
            </div>

            {/* Recent Achievements */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-400 text-sm">Recent Badges</span>
                <Award className="w-5 h-5 text-purple-400" />
              </div>
              {recentAchievements.length > 0 ? (
                <div className="space-y-2">
                  {recentAchievements.map((achievement, idx) => (
                    <motion.div
                      key={achievement.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="text-xl">{achievement.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{achievement.name}</p>
                        <p className="text-slate-400 text-xs">+{achievement.points} pts</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-xs">
                  Complete activities to unlock badges.
                </p>
              )}
            </div>

            {/* Total Achievements */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
                <Star className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300 font-semibold text-sm">
                  {unlockedAchievements.length} / {Object.keys(achievements).length} Achievements
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default GamificationWidget;
